// Cloudflare Turnstile CAPTCHA widget for bot protection on public forms
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Box, Typography } from '@mui/material';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'; // Test key fallback

export interface TurnstileWidgetRef {
  reset: () => void;
  getResponse: () => string | undefined;
}

interface TurnstileWidgetProps {
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
}

export const TurnstileWidget = forwardRef<TurnstileWidgetRef, TurnstileWidgetProps>(
  ({ onSuccess, onError, onExpire }, ref) => {
    const turnstileRef = useRef<TurnstileInstance>(null);

    useImperativeHandle(ref, () => ({
      reset: () => turnstileRef.current?.reset(),
      getResponse: () => turnstileRef.current?.getResponse(),
    }));

    return (
      <Box sx={{ my: 2 }}>
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={onSuccess}
          onError={() => onError?.()}
          onExpire={() => {
            onExpire?.();
            turnstileRef.current?.reset();
          }}
          options={{
            theme: 'light',
            size: 'normal',
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Protected by Cloudflare Turnstile
        </Typography>
      </Box>
    );
  }
);

TurnstileWidget.displayName = 'TurnstileWidget';
